<template lang="pug">
#draft-message-list(v-cloak)
  DraftMessageTable(
    v-model="items"
    :fields="tableFields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isLoading="isLoading"
    @onEdit="onEdit"
    @onDelete="onDelete"
    :familyMap="familyMap"
    :waiterFamilyMap="waiterFamilyMap"
    :otherUserGroupMemberMap="otherUserGroupMemberMap"
    :sendMessageGroupMap="sendMessageGroupMap"
    :residentGroupMap="residentGroupMap"
    :otherUserGroupMap="otherUserGroupMap"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(
        id="per-page-select"
        v-model="perPage"
        :options="pageOptions"
        size="md"
        @change="selectPerPage"
      )
    // ページネーション
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isLoading"
      @change="selectPage"
      hide-goto-end-buttons
    )

  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="doDelete")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。" @ok="createView")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { PcUrl } from "@/commons/constants/PcUrl";

import Setting from "../../../../public/setting.json";
import DraftMessagesService, { DraftMessagesGetModel } from "@/commons/services/DraftMessages";
import FamiliesService, { FamilyGetModel } from "@/commons/services/Families";
import WaiterFamiliesService, { WaiterFamiliesGetModel } from "@/commons/services/WaiterFamilies";
import OtherUserGroupMembersService from "@/commons/services/OtherUserGroupMembers";
import OtherUserGroupMemberModel from "@/commons/models/OtherUserGroup";
import SendMessageGroupsService, { SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroup from "@/commons/models/OtherUserGroup";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import DraftMessageTable, { DraftMessageTableField, DraftMessageTableItem } from "@/pc/containers/sendMessages/DraftMessageTable.vue";
import { SendMessageType } from "@/commons/services/SendMessages";

/**
 * ### メッセージ下書き一覧コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    DraftMessageTable
  }
})
export default class DraftMessageList extends Vue {
  /** メッセージ下書きサービス */
  @Inject("DraftMessagesService") draftMessagesService!: DraftMessagesService;
  /** 家族サービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 待機者家族サービス */
  @Inject("WaiterFamiliesService") waiterFamiliesService!: WaiterFamiliesService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupMembersService") otherUserGroupMembersService!: OtherUserGroupMembersService;
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** メッセージ下書きテーブル列名 */
  tableFields: Array<DraftMessageTableField> = [];
  /** メッセージ下書きテーブル各行の情報 */
  items: Array<DraftMessageTableItem> = [];
  /** メッセージ下書きテーブルで使用する家族名変換用MAP */
  familyMap: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();
  /** メッセージ下書きテーブルで使用する家族名変換用MAP */
  waiterFamilyMap: Map<string, string> = new Map<string, string>();
  /** メッセージ下書きテーブルで使用する施設関係者グループ変換用MAP */
  otherUserGroupMemberMap: Map<string, string> = new Map<string, string>();
  /** メッセージ下書きテーブルで使用する宛先グループ変換用MAP */
  sendMessageGroupMap: Map<string, SendMessageGroupsGetModel> = new Map<string, SendMessageGroupsGetModel>();
  /** メッセージ下書きテーブルで使用する利用者グループ変換用MAP */
  residentGroupMap: Map<string, ResidentGroupsGetModel> = new Map();
  /** メッセージ下書きテーブルで使用する施設関係者グループ変換用MAP */
  otherUserGroupMap: Map<string, OtherUserGroup> = new Map();
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** 削除対象の利用者ID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.draftMessage.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.draftMessage.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);

    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `draft-message-list-per-page=${perPage}; max-age=${saveSecond};`;

    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const tableFields: Array<DraftMessageTableField> = this.createTableFields();
    this.tableFields = tableFields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.draftMessage.perPage);

    await this.initialize();

    return;
  }

  /**
   * ### メッセージ下書き・承認依頼テーブルの列名を作成する
   *
   * - 送信先、宛先、タイトル、ステータス（下書き/承認待ち/承認済み/差戻し）、操作ボタン、作成者
   */
  public createTableFields(): Array<DraftMessageTableField> {
    let fields: Array<DraftMessageTableField> = [];

    fields.push({ key: "status", label: "状態", sortable: false, sortDirection: "desc", class: "status text-center" });
    fields.push({ key: "sendTarget", label: "送信先", sortable: false, sortDirection: "desc", class: "send-target text-center" });
    fields.push({ key: "targets", label: "宛先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "title", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "operation", label: "操作", sortable: false, sortDirection: "desc", class: "operation text-center" });
    fields.push({ key: "writtenBy", label: "作成者", sortable: false, sortDirection: "desc", class: "written-by" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = Number(cookieMap["draft-message-list-per-page"]);

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    // APIを並列で呼び出して待機時間を大幅に短縮する
    const [families, waiterFamilies, otherUserGroupMembers, sendMessageGroups, residentGroups, otherUserGroups, draftMessages] = await Promise.all([
      this.getFamilies(),
      this.getWaiterFamilies(),
      this.getOtherUserGroupMembers(),
      this.getSendMessageGroups(),
      this.getResidentGroups(),
      this.getOtherUserGroups(),
      this.getDraftMessages()
    ]);

    this.familyMap = this.createFamilyMap(families);
    this.waiterFamilyMap = this.createWaiterFamilyMap(waiterFamilies);
    this.otherUserGroupMemberMap = this.createOtherUserGroupMemberMap(otherUserGroupMembers);
    this.sendMessageGroupMap = this.createSendMessageGroupMap(sendMessageGroups);
    this.residentGroupMap = this.createResidentGroupMap(residentGroups);
    this.otherUserGroupMap = this.createOtherUserGroupMap(otherUserGroups);

    // メッセージ下書きテーブルに渡す各プロパティを作成する
    const filterdDraftMessages: Array<DraftMessagesGetModel> = this.filteringByType(draftMessages);

    // 全体の行数を設定する（別メソッドでの二重取得を廃止）
    this.rows = filterdDraftMessages.length;

    const items: Array<DraftMessageTableItem> = this.createTableItems(filterdDraftMessages);
    this.items = items;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   * @deprecated createView内で件数を設定するように変更したため廃止
   */
  public async setRows() {
    return;
  }

  /**
   * ### 施設にひもづく家族を取得する
   */
  public async getFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      response = await this.familiesService.getsInFacility(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 家族名変換用MAPを作成する
   *
   * - key：ソートキー
   * - value：家族名
   */
  public createFamilyMap(families: Array<FamilyGetModel>): Map<string, FamilyGetModel> {
    const map: Map<string, FamilyGetModel> = new Map<string, FamilyGetModel>();
    for (const family of families) {
      map.set(family.residentsSortKey, family);
    }
    return map;
  }

  /**
   * ### 施設にひもづく家族を取得する
   */
  public async getWaiterFamilies(): Promise<Array<WaiterFamiliesGetModel>> {
    const response: Array<WaiterFamiliesGetModel> = await this.waiterFamiliesService.getsInFacility(userStore);
    return response;
  }

  /**
   * ### 家族名変換用MAPを作成する
   *
   * - key：ソートキー
   * - value：家族名
   */
  public createWaiterFamilyMap(families: Array<WaiterFamiliesGetModel>): Map<string, string> {
    const map: Map<string, string> = new Map<string, string>();

    for (const family of families) {
      map.set(family.residentsSortKey, family.familyName);
    }

    return map;
  }

  /**
   * ### 施設にひもづく施設関係者グループメンバーを取得する
   */
  public async getOtherUserGroupMembers(): Promise<Array<OtherUserGroupMemberModel>> {
    const response: Array<OtherUserGroupMemberModel> = await this.otherUserGroupMembersService.get(userStore);
    return response;
  }

  /**
   * ### 施設関係者グループメンバー名変換用MAPを作成する
   *
   * - key：ソートキー
   * - value：メンバー名
   */
  public createOtherUserGroupMemberMap(otherUserGroupMembers: Array<OtherUserGroupMemberModel>): Map<string, string> {
    const map: Map<string, string> = new Map<string, string>();

    for (const member of otherUserGroupMembers) {
      map.set(member.otherUserGroupsSortKey, member.memberName);
    }

    return map;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroups(): Promise<Array<SendMessageGroupsGetModel>> {
    const response: Array<SendMessageGroupsGetModel> = await this.sendMessageGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 宛先グループ変換用MAPを作成する
   *
   * - key：ソートキー
   * - value：宛先グループ
   */
  public createSendMessageGroupMap(sendMessageGroups: Array<SendMessageGroupsGetModel>): Map<string, SendMessageGroupsGetModel> {
    const map: Map<string, SendMessageGroupsGetModel> = new Map<string, SendMessageGroupsGetModel>();
    for (const group of sendMessageGroups) {
      map.set(group.sendMessageGroupsSortKey, group);
    }
    return map;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループMAPを作成する
   *
   * - 利用者グループの場合、targetsにはprefixに「residentGroup」が付与されているのでここで付与する
   */
  createResidentGroupMap(residentGroups: Array<ResidentGroupsGetModel>): Map<string, ResidentGroupsGetModel> {
    const map: Map<string, ResidentGroupsGetModel> = new Map<string, ResidentGroupsGetModel>();
    for (const group of residentGroups) {
      map.set(`residentGroup_${group.id}`, group);
    }
    return map;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroup>> {
    const response: Array<OtherUserGroup> = await this.otherUserGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 施設関係者グループMAPを作成する
   *
   * - 施設関係者グループの場合、targetsにはprefixに「otherUserGroup」が付与されているのでここで付与する
   */
  createOtherUserGroupMap(otherUserGroups: Array<OtherUserGroup>): Map<string, OtherUserGroup> {
    const map: Map<string, OtherUserGroup> = new Map<string, OtherUserGroup>();
    for (const group of otherUserGroups) {
      map.set(`otherUserGroup_${group.id}`, group);
    }
    return map;
  }

  /**
   * ### メッセージ下書きを作成する
   */
  public async getDraftMessages(): Promise<Array<DraftMessagesGetModel>> {
    const draftMessages: Array<DraftMessagesGetModel> = await this.draftMessagesService.gets(userStore);
    return draftMessages;
  }

  /**
   * ### 下書きメッセージを種別でフィルタリングする
   */
  public filteringByType(draftMessages: Array<DraftMessagesGetModel>): Array<DraftMessagesGetModel> {
    return draftMessages.concat().filter((message) => {
      // TODO 設計バグ：画面やAPIでメッセージ送信を管理する場合はstring、下書きの種別はnumberで持ってしまっているので変換（String(message.type)）が必要
      switch (String(message.type)) {
        case undefined: // 過去の宛先を指定していない下書きはundefinedなので利用者家族メッセージの下書きとして扱う
        case SendMessageType.ALL_FAMILY:
        case SendMessageType.SELECTED_FAMILY:
        case SendMessageType.SELECTED_OTHER_USER_GROUP:
        case SendMessageType.SEND_MESSGE_GROUP: {
          return true;
        }
        case SendMessageType.ALL_WAITER:
        case SendMessageType.SELECTED_WAITER: {
          return false;
        }
        default: {
          return false;
        }
      }
    });
  }

  /**
   * ### メッセージ下書きの行を作成する
   */
  public createTableItems(draftMessages: Array<DraftMessagesGetModel>): Array<DraftMessageTableItem> {
    const items: Array<DraftMessageTableItem> = [];
    for (const draftMessage of draftMessages) {
      items.push({
        ...draftMessage,
        type: draftMessage.type !== undefined && draftMessage.type !== null ? (String(draftMessage.type) as SendMessageType) : undefined
      });
    }
    return items;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 新規送信タブに選択されたメッセージ下書きを復元する
   *
   * - 親コンポーネントにメッセージ下書きを渡して親コンポーネント経由で新規送信タブに内容を反映させる
   */
  public onEdit(draftMessage: DraftMessageTableItem): void {
    this.$emit("restoreDraftMessage", draftMessage);
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public onDelete(draftMessage: DraftMessageTableItem): void {
    const tiltle: string = draftMessage.title != null ? draftMessage.title : "";
    this.deleteMessage = `「${tiltle}」の下書きを削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = draftMessage.id;

    return;
  }

  /**
   * ### 下書きメッセージを削除する
   */
  public async doDelete(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.draftMessagesService.delete(userStore, this.deleteTargetId);

    // itemsから削除した利用者を削除するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: DraftMessageTableItem) => {
      if (item.id == this.deleteTargetId) {
        return false;
      } else {
        return true;
      }
    });
    this.rows--;

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 画面の作り直し
   *
   * - 親コンポーネントから呼び出される
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### 画面の作り直し
   */
  public async refreshByNoLoading(): Promise<void> {
    try {
      await this.createView();
    } catch (error) {
      // 処理中に画面遷移が発生した場合、エラーと見なさないためURLチェックする
      if (this.$route.fullPath != PcUrl.SendMessage) {
        return;
      } else {
        throw error;
      }
    }
    return;
  }

  public deleteDraftMessage(draftMessageId: number): void {
    this.items = this.items.filter((message) => {
      return message.id != draftMessageId ? true : false;
    });
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#draft-message-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
}
</style>
